// 전역 토스트 디스패처 (모바일 포팅 — 웹 lib/toast.js 이식)
// - window.CustomEvent 기반 — Provider 없이 어디서든 호출. 수신부 = ToastHost.tsx (TabLayout 마운트)
// - icon은 CSIcon name 문자열 (임시) — 비주얼 트랙에서 캐릭터/커스텀 아이콘으로 교체 예정
//   (효신님 결정: 기능 먼저, 아이콘 임시 CSIcon)

const EVENT = "sotda_toast";

export type ToastKind =
  | "levelup"
  | "achievement"
  | "attendance"
  | "quest"
  | "success"
  | "error"
  | "info";
export type ToastPayload = {
  kind: ToastKind;
  title: string;
  subtitle?: string | null;
  icon?: string;     // ⚠ 죽은 필드 — 렌더는 kind로 결정한다(게임=CastAvatar / 시스템=SystemIcon).
                     //   기존 호출 호환을 위해 타입만 남긴다. 새 호출엔 쓰지 말 것.
  expr?: string;     // 게임 계열 캐릭터 표정 — confident(기본)/celebrate/surprised/focused/sad/sleepy.
                     //   생략하면 kind별 기본 표정(ToastHost EXPR_BY_KIND). 상황에 맞게 덮어쓸 때만 지정.
  tierKey?: string | null;
  link?: string;     // 클릭 시 이동 경로 (react-router) — 없으면 클릭 불가 (6번 2026-06-24)
};

/** 범용 토스트 발송
 * - 문자열 안전망: `showToast('메시지')`처럼 문자열을 넘기면 ToastHost가 detail을 spread할 때
 *   인덱스로 분해돼 title=undefined → "별 아이콘만 뜨는 빈토스트" 버그가 났음(@ts-nocheck 화면들).
 *   여기서 문자열을 받으면 `{ kind:'info', title }`로 자동 래핑해 근본 차단.
 *   메시지 앞 ✅🎯🔔 등 이모지가 의미를 전달(기존 객체 호출도 title 이모지+icon 병존 패턴).
 */
export function showToast(payload: ToastPayload | string) {
  if (typeof window === "undefined") return;
  const detail: ToastPayload =
    typeof payload === "string" ? { kind: "info", title: payload } : payload;
  window.dispatchEvent(new CustomEvent(EVENT, { detail }));
}

/** 시스템 성공 알림 */
export function showSuccess(title: string, subtitle?: string | null, link?: string): void {
  showToast({ kind: "success", title, subtitle, link });
}

/** 시스템 오류 알림 */
export function showError(title: string, subtitle?: string | null, link?: string): void {
  showToast({ kind: "error", title, subtitle, link });
}

/** 시스템 안내 알림 */
export function showInfo(title: string, subtitle?: string | null, link?: string): void {
  showToast({ kind: "info", title, subtitle, link });
}

/** 레벨업 — "Lv.7 달성!" */
export function showLevelUpToast({ newLevel, tierKey }: { newLevel: number; tierKey?: string | null }) {
  showToast({
    kind: "levelup",
    title: `Lv.${newLevel} 달성!`,
    subtitle: tierKey ? `${tierKey} 티어` : null,
    icon: "star",
    tierKey,
    link: "/my", // 레벨/등급 = MY 탭 (6번 2026-06-24)
  });
}

/** 업적 해금 — ach.name / ach.description */
export function showAchievementToast(ach: { name: string; description?: string | null; icon?: string }) {
  showToast({
    kind: "achievement",
    expr: "celebrate",          // 업적 해금은 축하 상황 — 기본 confident보다 얼굴이 맞는다
    title: ach.name,
    subtitle: ach.description || null,
    link: "/collection", // 업적 갤러리 = 컬렉션 탭 (6번 2026-06-24)
  });
}

/** 출석 완료 */
export function showAttendanceToast({ streak = 1, bonus = 0 }: { streak?: number; bonus?: number }) {
  const streakMsg = streak > 1 ? ` · ${streak}일 연속` : "";
  const bonusMsg = bonus > 0 ? ` · 보너스 +${bonus}` : "";
  showToast({
    kind: "attendance",
    title: "오늘 출석 완료",
    subtitle: `+10 EXP${streakMsg}${bonusMsg}`,
    icon: "flame",
  });
}

// 일일 숙제 3슬롯 완주 — "기여" 뉘앙스 5개 랜덤 로테이션 (이모지 제거, 텍스트만)
const QUEST_COMPLETE_MESSAGES = [
  "황소 커뮤니티에 활력 +1",
  "오늘도 판 달궈줘서 고마워요",
  "커뮤니티 체온 +1℃ 올렸어요",
  "덕분에 오늘 커뮤니티가 돌아갔어요",
  "황소 한 마리 더 움직였다",
];

/** 일일 숙제 완주 — 랜덤 격려 + 30 EXP */
export function showQuestCompleteToast() {
  const msg = QUEST_COMPLETE_MESSAGES[Math.floor(Math.random() * QUEST_COMPLETE_MESSAGES.length)];
  showToast({ kind: "quest", title: msg, subtitle: "+30 EXP", icon: "sparkle" });
}

export const TOAST_EVENT = EVENT;
